import React from 'react'
import { InputVariant } from './InputVariant.type'

type IconPosition = 'left' | 'right'

export type InputProps = {
  name?: string
  type?: string
  id?: string
  placeholder?: string
  reference?: React.RefObject<HTMLInputElement>
  error?: boolean
  label?: string
  message?: string
  maxLength?: number
  testId?: string
  variant?: InputVariant
  helpText?: string
  reversed?: boolean
  disabled?: boolean
  iconPosition?: IconPosition
  icon?: React.ReactNode
  fixedEmailSuffix?: string
  describedById?: string
  groupClassName?: string
  inputClassName?: string
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
  pattern?: string
  minWidth?: string
}

export const Input = (props: InputProps) => {
  const {
    name,
    type,
    id,
    placeholder,
    reference,
    error,
    label,
    message,
    maxLength,
    testId,
    variant,
    helpText,
    reversed,
    disabled,
    iconPosition,
    icon,
    fixedEmailSuffix = '@ampf.com',
    describedById,
    groupClassName,
    inputClassName,
    inputMode,
    pattern,
    minWidth
  } = props

  const isFloatingLabel = variant === InputVariant.FLOATING_LABEL
  const hasIconModifier =
    variant === InputVariant.WITH_ICON || typeof iconPosition !== 'undefined'
  const resolvedIconPosition = iconPosition || 'right'
  const isNumericVariant = variant === InputVariant.NUMERIC
  const isError = Boolean(error)
  const hasErrorMessage = isError && Boolean(message)
  const hasHelpText = Boolean(helpText)

  const helpListId = describedById || (id ? `${id}-error-help` : undefined)
  const shouldDescribeInput = hasErrorMessage || hasHelpText

  const inputClasses = [
    'Input',
    isFloatingLabel ? 'Input--floatingLabel' : '',
    variant === InputVariant.FIXED_EMAIL ? 'Input--fixedEmail' : '',
    hasIconModifier ? 'Input--withIcon' : '',
    hasIconModifier && resolvedIconPosition === 'left'
      ? 'Input--withIconLeft'
      : '',
    isError ? 'Input--error' : '',
    reversed ? 'Input--reversed' : '',
    inputClassName || ''
  ]
    .filter(Boolean)
    .join(' ')

  const resolvedInputMode = isNumericVariant ? 'numeric' : inputMode
  const resolvedPattern = isNumericVariant ? '[0-9]*' : pattern

  const baseInput = (
    <input
      className={inputClasses}
      name={name}
      id={id}
      placeholder={placeholder}
      type={type || 'text'}
      ref={reference}
      maxLength={maxLength}
      disabled={disabled}
      aria-describedby={shouldDescribeInput ? helpListId : undefined}
      inputMode={resolvedInputMode}
      pattern={resolvedPattern}
      style={minWidth ? { minWidth } : undefined}
      data-testid={testId ? `${testId}-input` : undefined}
      data-qa={'Input-' + id}
    />
  )

  const renderHelpList = () => {
    if (!hasErrorMessage && !hasHelpText) {
      return null
    }

    return (
      <ul id={helpListId}>
        {hasErrorMessage && (
          <li
            className="Form-help Form-help--error"
            data-qa={'InputError-' + id}
            data-testid={testId ? `${testId}-message` : undefined}
          >
            <span
              data-testid={testId ? `${testId}-warning` : undefined}
              aria-hidden="true"
            >
              warning
            </span>
            <span className="u-hiddenVisually">Error,</span>
            <span>{message}</span>
          </li>
        )}
        {hasHelpText && <li className="Form-help">{helpText}</li>}
      </ul>
    )
  }

  return (
    <div
      className={['Form-group', groupClassName || ''].filter(Boolean).join(' ')}
    >
      {!isFloatingLabel && label && (
        <label
          className="Form-label"
          htmlFor={id}
          data-testid={testId ? `${testId}-label` : undefined}
        >
          {label}
        </label>
      )}

      {isFloatingLabel ? (
        <div className="Input-group">
          {baseInput}
          <label
            className="Input-label"
            htmlFor={id}
            data-testid={testId ? `${testId}-label` : undefined}
          >
            {label}
          </label>
        </div>
      ) : variant === InputVariant.WITH_ICON ? (
        <div className="Input-group">
          {baseInput}
          <span className="Input-icon" aria-hidden="true">
            {icon || '\ue8b6'}
          </span>
        </div>
      ) : variant === InputVariant.FIXED_EMAIL ? (
        <div className="Input-group--flex">
          {baseInput}
          <p className="Input-email">{fixedEmailSuffix}</p>
        </div>
      ) : (
        baseInput
      )}

      {renderHelpList()}
    </div>
  )
}
